import React, {Component} from 'react';
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Button
} from 'reactstrap';
import ReactTable from 'react-table';
import {withTranslation} from 'react-i18next';
import {Colxx} from '../components/common/CustomBootstrap';
import ModalManager from '../components/modal/ModalManager';
import {reactTableText} from '../constants/configs';
import {find, keyBy, mapValues} from 'lodash';
import {getStatusColor, i18nizeDate} from '../helpers/Utils';
import {NotificationManager} from '../components/common/react-notifications';
import ReactTableTextFilter from '../components/ReactTableTextFilter';
import BankAccountDetails from './BankAccountDetails';
import BankAccountForm from './BankAccountForm';
import Api from '../services/Api';
import Rbac from '../components/Rbac';

class BankAccounts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      payIds: [],
      accounts: [],
      pages: -1,
      loading: false,
      expanded: props.filteredAccountNumber ? {0: true} : {},
    };
  }

  componentDidMount() {
    if (Rbac.hasPermission("view_bank-deposit")) {
      this.fetchPaymentIds();
    }
  }

  fetchPaymentIds = async () => {
    try {
      const {data} = await Api.invoke(
        'GET',
        '/v2/admin-ui/deposits/paymentIds',
        null,
        {
          params: {
            userId: this.props.userId,
            status: "VERIFIED",
            limit: 100
          },
        },
      );
      this.setState({
        payIds: data.rows,
      });
    } catch (e) {
      console.log("fetchPaymentIds error: ", e);
    }
  };

  fetchData = async state => {
    try {
      this.setState({loading: true});
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/admin-ui/user-bank-accounts',
        null,
        {
          params: {
            userId: this.props.userId,
            offset,
            limit: state.pageSize,
            ...(state.filtered.length ? mapValues(keyBy(state.filtered, 'id'), 'value') : null),
          }
        },
      );
      this.setState({
        accounts: data.accounts,
        pages: data.totalPage,
      });
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  deleteBankAccount = async accountId => {
    const {t, userId} = this.props;
    try {
      await Api.invoke(
        'DELETE',
        '/admin-ui/user-bank-account',
        {
          accountId,
          userId
        },
      );
      NotificationManager.success(t('Bank account deleted successfully'), t('Message'));
      this.table.fireFetchData();
    } catch (e) {
      console.log('error: ', e);
    }
  };

  onPressDelete = accountId => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Confirm Delete'),
      body: t('Are you sure you want to delete this bank account ?'),
      buttons: [
        {
          color: 'primary',
          onClick: async () => {
            this.deleteBankAccount(accountId);
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Accept'),
        },
        {
          color: 'grey',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Close'),
        },
      ],
    });
  };

  renderBankAccountForm = bankAccount => {
    return (
      <BankAccountForm
        bankAccount={bankAccount}
        userId={this.props.userId}
        fireFetchData={() => {this.table.fireFetchData();}}
      />
    );
  };

  onPressEdit = bankAccount => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Edit Bank Account'),
      body: this.renderBankAccountForm(bankAccount),
    });
  };

  onPressCreate = () => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Create Bank Account'),
      body: this.renderBankAccountForm(),
    });
  };

  onExpanded = (newExpanded, index) => {
    this.setState({
      expanded: {
        ...this.state.expanded,
        [index[0]]: !!newExpanded[index[0]],
      },
    });
  };

  generatePayId = async iban => {
    const {t, userId} = this.props;
    try {
      await Api.invoke(
        'POST',
        '/v2/admin-ui/deposits/paymentIds',
        {
          userId,
          sourceIbans: [iban],
        },
      );
      await this.fetchPaymentIds();
      NotificationManager.success(t('Payment id created successfully'), t('Message'));
    } catch (e) {
      console.log('error: ', e);
    }
  };

  getColumns = t => [
    {
      Header: t('Account Number'),
      accessor: 'accountNumber',
      width: 140,
      filterable: true,
      Filter: ReactTableTextFilter,
    },
    {
      Header: t('Card Number'),
      accessor: 'cardNumber',
      width: 180,
      filterable: true,
      Filter: ReactTableTextFilter,
      //Cell: props => <span>{mask(props.value, '####-####-####-####')}</span>,
    },
    {
      Header: t('National Account Number'),
      accessor: 'nationalAccountNumber',
      width: 240,
      filterable: true,
      Filter: ReactTableTextFilter,
      Cell: props => <span>{props.value ? `IR${props.value}` : null}</span>,
    },
    {
      Header: t('Deposit Id'),
      accessor: 'nationalAccountNumber',
      width: 200,
      Cell: props => {
        const paymentId = find(this.state.payIds, item => item.sourceIbans[0] === props.value);
        return (
          <span>{paymentId ? paymentId.payId : "-------"}</span>
        );
      },
      show: Rbac.hasPermission('view_bank-deposit'),
    },
    {
      Header: t('Status'),
      accessor: 'status',
      filterable: true,
      Filter: ({filter, onChange}) => (
        <select
          style={{width: '100%'}}
          value={filter ? filter.value : t('all')}
          onChange={event => onChange(event.target.value)}
        >
          <option value="">{t('all')}</option>
          <option value="Pending">{t('Pending')}</option>
          <option value="Rejected">{t('Rejected')}</option>
          <option value="Verified">{t('Verified')}</option>
        </select>
      ),
      Cell: props => <span className={getStatusColor(props.value)}>{t(props.value)}</span>,
    },
    {
      Header: t('Update Date'),
      accessor: 'updatedAt',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
    },
    {
      Header: t('Actions'),
      Cell: props => {
        const paymentId = find(this.state.payIds, item => item.sourceIbans[0] === props.original.nationalAccountNumber);
        return (
          <div className="flex align-items-center">
            <Rbac permission="delete_user-bank-accounts">
              <div className="text-danger my-2 cursor-pointer"
                    onClick={() => {this.onPressDelete(props.original.id);}}>
                {t('Delete')}
              </div>
            </Rbac>
            <Rbac permission="update_user-bank-accounts">
              <div className="text-info my-2 cursor-pointer"
                    onClick={() => {this.onPressEdit(props.original);}}>
                {t('Edit')}
              </div>
            </Rbac>
            {!paymentId && <Rbac permission="update_bank-deposit">
              <div className="text-success my-2 cursor-pointer"
                   onClick={async () => {
                     await this.generatePayId(props.original.nationalAccountNumber);
                   }}>
                {t('Generate PayId')}
              </div>
            </Rbac>}
          </div>
        );
      },
      show: Rbac.hasPermission('update_user-bank-accounts') ||
        Rbac.hasPermission('delete_user-bank-accounts') ||
        Rbac.hasPermission('update_bank-deposit'),
      width: 150,
    },
  ];

  updateList = (values, index) => {
    const {accounts} = this.state;
    let list = [...accounts];
    list.splice(index, 1, {...accounts[index], ...values});
    this.setState({accounts: list});
  };

  render() {
    const {t} = this.props;
    return (
      <Row>
        <Colxx xxs="12" className="mb-4">
          <Card className="h-100">
            <CardBody>
              <CardTitle className="text-large text-primary d-flex flex-row justify-content-between align-items-center">
                {t('Bank Accounts')}
                <Rbac permission='create_user-bank-accounts'>
                  <Button onClick={this.onPressCreate}>
                    <i className="simple-icon-plus add-reply-icon"/>
                    <span className="mx-1">{t('Add')}</span>
                  </Button>
                </Rbac>
              </CardTitle>
              <ReactTable
                manual
                ref={el => {this.table = el;}}
                className="-highlight"
                data={this.state.accounts}
                pages={this.state.pages}
                loading={this.state.loading}
                sortable={false}
                resizable={true}
                expanded={this.state.expanded}
                onPageChange={() => {this.setState({expanded: {}});}}
                onExpandedChange={this.onExpanded}
                onFetchData={this.fetchData}
                columns={this.getColumns(t)}
                defaultPageSize={5}
                style={{
                  width: '100%'
                }}
                defaultFiltered={[
                  {
                    id: 'accountNumber',
                    value: this.props.filteredAccountNumber,
                  }
                ]}
                {...reactTableText}
                SubComponent={row =>
                  <BankAccountDetails
                    itemIndex={row.index}
                    bankAccount={row.original}
                    updateList={this.updateList}
                  />
                }
              />
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }
}

export default withTranslation()(BankAccounts);
